/**
 * IndexedDB storage for in-progress drafts and queued submissions.
 *
 * Connectivity at registration desks is unreliable, so a half-finished form
 * survives a reload, a dead battery or a tab crash, and a completed submission
 * waits in a queue rather than being lost.
 */

import type { ApplicationData, GuardianLink, SubmissionResult } from '@mbs/shared';

const DB_NAME = 'mbs-registration';
const DB_VERSION = 1;
export const DRAFT_STORE = 'drafts';
export const QUEUE_STORE = 'queue';

export interface DraftRecord {
  id: string;
  updated_at: string;
  branch: string;
  /** Whatever the form component owns; typed loosely so the form can evolve. */
  data: Record<string, unknown>;
}

/** Progress carried between attempts so a retry never redoes finished work. */
export interface SubmissionProgress {
  guardians?: GuardianLink[];
  school_id?: string;
}

export interface QueueRecord {
  id: string;
  created_at: string;
  updated_at: string;
  label: string;
  applicant_type: 'New' | 'Existing';
  branch_code: 'M1' | 'M2' | 'DD';
  /** Guardian payloads, still to be upserted. */
  guardians: unknown[];
  application: Partial<ApplicationData>;
  progress: SubmissionProgress;
  attempts: number;
  last_error?: string;
  result?: SubmissionResult;
  status: 'pending' | 'done' | 'failed';
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(DRAFT_STORE)) db.createObjectStore(DRAFT_STORE, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(QUEUE_STORE)) db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return dbPromise;
}

async function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(store, mode);
    const request = run(transaction.objectStore(store));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export const drafts = {
  save: (record: DraftRecord) => tx(DRAFT_STORE, 'readwrite', (s) => s.put(record)),
  get: (id: string) => tx<DraftRecord | undefined>(DRAFT_STORE, 'readonly', (s) => s.get(id)),
  all: () => tx<DraftRecord[]>(DRAFT_STORE, 'readonly', (s) => s.getAll()),
  remove: (id: string) => tx(DRAFT_STORE, 'readwrite', (s) => s.delete(id)),
};

export const queue = {
  put: (record: QueueRecord) => tx(QUEUE_STORE, 'readwrite', (s) => s.put(record)),
  get: (id: string) => tx<QueueRecord | undefined>(QUEUE_STORE, 'readonly', (s) => s.get(id)),
  all: () => tx<QueueRecord[]>(QUEUE_STORE, 'readonly', (s) => s.getAll()),
  remove: (id: string) => tx(QUEUE_STORE, 'readwrite', (s) => s.delete(id)),
};
